/**
 * Admin Dashboard Page
 * Admin-only page for managing the platform
 * Enhanced version with comprehensive platform management
 * Single-page scrollable layout with inline module sections
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import AdminAffiliates from './AdminAffiliates';
import SEOHead from '../components/SEOHead';
import { isAdmin, isAdminUser } from '../utils/admin';
import { supabase } from '../lib/supabase';
import {
  UsersIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowTrendingUpIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  BellIcon,
  ClockIcon,
  CurrencyDollarIcon,
  BoltIcon,
  CpuChipIcon, // Using CpuChipIcon for "Network" concept or similar connectivity
  EyeIcon,
  EnvelopeIcon,

  DocumentTextIcon,
  UserIcon,
  MagnifyingGlassIcon,
  ViewfinderCircleIcon,
  FunnelIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  NoSymbolIcon,
  CheckIcon, // CheckSquare -> CheckIcon or CheckCircleIcon; usually CheckIcon is fine + css border
  Square2StackIcon, // "Square" -> potentially StopIcon or just a square shape? Square2Stack is 'Package'-like. Let's use StopIcon for outline square. Actually generic 'Square' isn't in v2 outline easily named 'Square'. We can use basic shapes or just empty div. But Lucide Square is just a box. `StopIcon` is a solid square usually?. Let's use `StopIcon` or just `QueueListIcon` if list. 
  // Wait, Lucide Square is checkbox unchecked. Heroicons: `StopIcon` (solid?) or creating custom. 
  // Let's use `StopIcon` for now or omit if unused.
  ArchiveBoxIcon,
  BookOpenIcon,
  PaperAirplaneIcon,
  CalendarIcon,
  FolderOpenIcon,
  ArrowsPointingOutIcon,
  InboxIcon,
  PhotoIcon,
  CameraIcon,
  LinkIcon,
  ChatBubbleLeftRightIcon,
  MegaphoneIcon,
  BuildingStorefrontIcon,
  ChevronDownIcon,
  BanknotesIcon,
  PrinterIcon,
  LifebuoyIcon,
  EllipsisHorizontalIcon,
  CloudArrowUpIcon,
  Squares2X2Icon,
  ScaleIcon,
} from '@heroicons/react/24/outline';

import ErrorBoundary from '../components/ErrorBoundary';
import { LoadingOverlay } from '../components/Loading';
import { ProductCostManagement } from '../components/ProductCostManagement';
import BlogManagement from '../components/BlogManagement';
import NewsletterManagement from '../components/NewsletterManagement';
import BlogSubmissionReview from '../components/BlogSubmissionReview';
import SendExistingPublicationEmailsButton from '../components/SendExistingPublicationEmailsButton';
import SendWelcomeEmailsButton from '../components/SendWelcomeEmailsButton';
import BrandAssets from '../components/BrandAssets';
import { UserAnalyticsView } from '../components/admin/UserAnalyticsView';
import SecretSantaAdmin from '../components/admin/SecretSantaAdmin';
import AffiliateAdminView from '../components/admin/AffiliateAdminView';
import AffiliateEmailComposer from '../components/admin/AffiliateEmailComposer';
import { AdminBentoCard, AdminBentoRow } from '../components/ui/admin-bento-card';
import { SiteAnalyticsCard } from '../components/admin/SiteAnalyticsCard';
import { CrmCard } from '../components/admin/CrmCard';
import { CalculatorCard } from '../components/admin/CalculatorCard';
import { CrmDirectory } from '../components/admin/CrmDirectory';
import { RefundsLedger } from '../components/admin/RefundsCard';
import ProfitGraph from '../components/admin/ProfitGraph';
import { DashboardCategoryCard } from '../components/admin/DashboardCategoryCard';
import { EditableTile } from '../components/admin/EditableTile';
import { useDashboardLayout } from '../components/admin/useDashboardLayout';
import { ExpandableScreen, ExpandableScreenTrigger, ExpandableScreenContent } from '../components/ui/expandable-screen';
import { AnimatedNumber } from '../components/ui/animated-number';
import { cn } from '../components/ui/utils';
import AdminUsersView from '../components/admin/AdminUsersView';
import AdminSettingsView from '../components/admin/AdminSettingsView';
import AdminMailView from '../components/admin/AdminMailView';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import AdminOverviewView from '../components/admin/AdminOverviewView';
import { DashboardCharts } from '../components/admin/DashboardCharts';
import AdminGalleryView from '../components/admin/AdminGalleryView';
import AdminRosterView from '../components/admin/AdminRosterView';
import AdminClientUploadsView from '../components/admin/AdminClientUploadsView';
import AdminSupportView from '../components/admin/AdminSupportView';
import { AdminProspectsView } from '../components/admin/AdminProspectsView';
import MoneyLedgerView from '../components/admin/MoneyLedgerView';
import AdminEventsView from '../components/admin/AdminEventsView';
import AdminPrintShopView from '../components/admin/AdminPrintShopView';
import AdminBookingView from '../components/admin/AdminBookingView';
import AdminCalendarView from '../components/admin/AdminCalendarView';
import AdminInvoices from './AdminInvoices';
import { RevenueTracker } from '../components/ui/revenue-tracker';
import { RevenueWidget } from '../components/ui/revenue-widget';
import { RegistryWidget } from '../components/ui/registry-widget';
import { AnalyticsWidget } from '../components/ui/analytics-widget';
import { CrmWidgetLive } from '../components/admin/CrmWidgetLive';
import { NotificationsWidgetLive } from '../components/admin/NotificationsWidgetLive';
import { ClockWidget } from '../components/ui/clock-widget';
import { DetailSheet } from '../components/ui/detail-sheet';
import { CalendarWidget } from '../components/ui/calendar-widget';
import { FitBox } from '../components/ui/fit-box';
import { MiniBars, Sparkline, StatusMarks } from '../components/ui/viz';
import { useLongPress } from '../components/ui/use-long-press';
import { WeatherWidget } from '../components/ui/weather-widget';
import CopyDebugReport from '../components/admin/CopyDebugReport';
import AdminDeploymentNotifications from '../components/admin/AdminDeploymentNotifications';
import WhatsNewModal from '../components/admin/tour/WhatsNewModal';
import { TOUR_PANEL_EVENT } from '../components/admin/tour/tours';
import { installGlobalListeners, logApiCall } from '../lib/adminErrorLog';

interface DashboardStats {
  totalUsers: number;
  activeSubscriptions: number;
  freeUsers: number;
  premiumUsers: number;
  proUsers: number;
  totalToolUsage: number;
  recentActivity: number;
  platformHealth: 'healthy' | 'warning' | 'critical';
  newsletterSubscribers: number;
  history: {
    revenue: (string | { date: string; amount: number })[];
    newsletter: string[];
    affiliates: string[];
    bookings: (string | { date: string; amount: number })[];
  };
  // Blog-specific metrics
  blogWriters?: number;
  totalBlogPosts?: number;
  publishedThisMonth?: number;
  affiliateRevenue?: number;
  galleryRevenue?: number;
  bookingRevenue?: number;
  /** Street nights (field_sales) — walk-up sales recorded once per night. */
  streetRevenue?: number;
  contributorDetails?: Array<{
    authorName: string;
    email: string;
    postCount: number;
    latestPost: string;
  }>;
  galleryPhotoCount?: number;
}

interface RecentUser {
  id: string;
  email: string;
  username: string | null;
  tier: string;
  isAdmin: boolean;
  created_at: string;
  resources?: {
    hasBlog: boolean;
    galleries: Array<{ id: string; name: string }>;
  };
}

interface BookClubPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  published_at: string | null;
  created_at: string;
  subdomain: string | null;
  published: boolean;
}

interface LostArchivesPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  published_at: string | null;
  created_at: string;
  published: boolean;
}

interface Alert {
  id: number;
  type: 'info' | 'warning' | 'success' | 'error';
  message: string;
  time: string;
  read: boolean;
}

const PLATFORM_LAUNCH_DATE = '2026-01-15';

/**
 * The instruments, as opposed to the data tiles.
 *
 * You point at a key, a day or a control inside these — not at the tile — and
 * each of those already lights on its own hover. The cell's tile-wide wash is
 * suppressed for them so hovering one key doesn't highlight the whole widget.
 * Data tiles keep the wash: there, the tile *is* the target.
 *
 * The calendar's 1x1 shape is the exception. It isn't the month grid, it's a
 * single date face with nothing inside it to light, and the whole tile opens
 * the master calendar — so it behaves like a data tile and keeps the wash.
 */
const TOOL_WIDGETS = new Set(['clock', 'calendar', 'calculator']);
const drawsOwnHover = (id: string, size: string) =>
  TOOL_WIDGETS.has(id) && !(id === 'calendar' && size === '1x1');

const isTestEmail = (email: string | null | undefined) => {
  if (!email) return false;
  const testPatterns = ['test', 'demo', 'admin', 'dev', 'staging', 'dummy'];
  return testPatterns.some(pattern =>
    email.toLowerCase().includes(pattern)
  );
};

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const { success, error: showError } = useToast();
  const navigate = useNavigate();
  const [adminActionsOpen, setAdminActionsOpen] = useState(false);
  const [userSubdomain, setUserSubdomain] = useState<string | null>(null);
  const [adminStatus, setAdminStatus] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [expandedPost, setExpandedPost] = useState<BookClubPost | LostArchivesPost | null>(null);
  const [bookClubPosts, setBookClubPosts] = useState<BookClubPost[]>([]);
  const [loadingBookClubPosts, setLoadingBookClubPosts] = useState(false);
  const [lostArchivesPosts, setLostArchivesPosts] = useState<LostArchivesPost[]>([]);
  const [loadingLostArchivesPosts, setLoadingLostArchivesPosts] = useState(false);
  const [registeredWriters, setRegisteredWriters] = useState<number>(0);
  const [censusCounts, setCensusCounts] = useState<{ posts: number; products: number; users: number | null }>({ posts: 0, products: 0, users: null });
  const [pendingSubmissions, setPendingSubmissions] = useState<number>(0);
  const [prodigiErrorCount, setProdigiErrorCount] = useState<number>(0);
  // Support tickets whose last word is the user's: someone is waiting on a reply.
  const [supportWaiting, setSupportWaiting] = useState<number>(0);
  const [prospectsWaiting, setProspectsWaiting] = useState<number>(0);
  // Leads the client-hunter agent found that still need a read — the dock badge.
  useEffect(() => {
    fetch('/api/admin/prospects?status=new')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.prospects) setProspectsWaiting(d.prospects.filter((p: any) => p.status === 'new').length); })
      .catch(() => {});
  }, []);
  const [recentPosts, setRecentPosts] = useState<Array<{ title: string; author: string; date: string }>>([]);
  const [newestSubscribers, setNewestSubscribers] = useState<Array<{ email: string; created_at: string }>>([]);
  const [affiliateStats, setAffiliateStats] = useState<{
    totalAffiliates: number;
    activeAffiliates: number;
    totalEarnings: number;
    totalClicks: number;
    totalConversions: number;
    conversionRate: number;
    pendingPayouts: number;
    totalMLMEarnings: number;
  } | null>(null);
  const [photoLibraries, setPhotoLibraries] = useState<any[]>([]);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [healthMetrics, setHealthMetrics] = useState<{
    db: boolean;
    auth: boolean;
    storage: boolean;
    api: boolean;
  }>({
    db: false,
    auth: false,
    storage: false,
    api: false
  });

  const [allUsers, setAllUsers] = useState<RecentUser[]>([]);

  // Ref for page top
  const pageTopRef = useRef<HTMLDivElement>(null);

  // Install global error/API listeners for debug report
  useEffect(() => { installGlobalListeners(); }, []);

  // Lock body scroll while admin page is mounted (no page scrolling on admin)
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Which console panel is open (null = none)
  const [activePanelSection, setActivePanelSection] = useState<string | null>(null);
  // ?panel=<id> opens a panel directly — used by the header date to jump
  // straight to the master calendar from anywhere in the admin.
  const [searchParams] = useSearchParams();
  // Watches the query rather than reading it once: clicking the header date
  // while already on /admin changes the search string but never remounts, so
  // a mount-only read left the panel closed.
  useEffect(() => {
    const panel = searchParams.get('panel');
    if (panel) setActivePanelSection(panel);
  }, [searchParams]);

  // Walkthroughs open and close panels here (see components/admin/tour).
  // Set, not toggled, so a step that re-opens the open panel is a no-op.
  useEffect(() => {
    const onPanel = (e: Event) => setActivePanelSection((e as CustomEvent<string | null>).detail ?? null);
    window.addEventListener(TOUR_PANEL_EVENT, onPanel);
    return () => window.removeEventListener(TOUR_PANEL_EVENT, onPanel);
  }, []);

  // Long press on the dashboard calendar opens the master calendar panel —
  // the same one the header date links to.
  const calendarPress = useLongPress(() => openPanel('calendar'));
  // The clock expands on hold only — plain clicks belong to the widget itself
  // (face click cycles format, label click cycles mode, per the clock rule).
  const [clockOpen, setClockOpen] = useState(false);
  const clockPress = useLongPress(() => setClockOpen(true), 450, { holdOnMouse: true });
  // Widget shapes and order, editable and saved to this browser.
  const layout = useDashboardLayout();
  // The grid's rows are set to exactly one column width, measured, so a tile
  // spanning N columns and N rows is square and every tile of a shape is the
  // same size. Without this the row height comes from whatever content lands in
  // a row, and two tiles both marked 2x2 render differently.
  const gridRef = useRef<HTMLDivElement>(null);
  /* The row unit is CSS now, not a measurement. It used to come from a
     ResizeObserver in an effect with empty deps that bailed when the grid
     was not mounted yet — and the dashboard renders a loading state first,
     so it bailed every time, never measured again, and the rows fell back
     to content height. That is why a 2x2 came out a rectangle and a 4x2
     came out smashed: the column spans were right and the row heights were
     never applied. Container query units give the same number with nothing
     to race. */
  // Ticks once a minute — enough for a date face.
  const [today, setToday] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setToday(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const openPanel = (id: string) => {
    setActivePanelSection(prev => (prev === id ? null : id));
  };

  // Deep-link support (e.g. /admin?section=invoices&booking_id=...) — opens a console
  // panel directly instead of requiring a separate route for each section.
  useEffect(() => {
    const section = searchParams.get('section');
    if (section) setActivePanelSection(section);
  }, [searchParams]);

  const unreadAlertsCount = alerts.filter(a => !a.read).length;


  useEffect(() => {
    checkAdminAccess();
  }, [user]);

  useEffect(() => {
    if (adminStatus === true) {
      loadDashboardData();
      loadBookClubPosts();
      loadLostArchivesPosts();
      loadBlogStats();

      // Set up auto-refresh every 30 seconds
      const refreshInterval = setInterval(() => {
        loadDashboardData();
        // Always refresh blog data since all modules are inline
        loadBlogStats();
        loadBookClubPosts();
        loadLostArchivesPosts();
      }, 30000);

      return () => clearInterval(refreshInterval);
    }
  }, [adminStatus, user]);

  // Listen for new blog submissions
  useEffect(() => {
    if (adminStatus !== true) return;

    const channel = supabase
      .channel('admin_dashboard_alerts')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'blog_submissions'
        },
        (payload) => {
          const newSubmission = payload.new as any;
          if (newSubmission.status === 'pending') {
            // Add new alert
            setAlerts(prev => [
              {
                id: Date.now(),
                type: 'info',
                message: `New blog submission: "${newSubmission.title}"`,
                time: 'Just now',
                read: false
              },
              ...prev
            ]);

            // Show toast
            success(`New submission received: ${newSubmission.title}`);

            // Refresh data if needed
            // loadDashboardData(); 
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [adminStatus, success]);

  // Load user subdomain for profile link
  useEffect(() => {
    if (user) {
      (async () => {
        try {
          const { data } = await supabase
            .from('user_subdomains')
            .select('subdomain')
            .eq('user_id', user.id)
            .maybeSingle();
          if (data?.subdomain) {
            setUserSubdomain(data.subdomain);
          }
        } catch (error) {
          // Ignore errors
          console.error('Error loading subdomain:', error);
        }
      })();
    }
  }, [user]);

  const checkAdminAccess = async () => {
    if (authLoading) return;

    if (!user) {
      navigate('/');
      return;
    }

    // First check: email / role match (fastest, no database query).
    // Case-insensitive and also honors admin role metadata, so the owner is
    // recognized whether signed in via password or Google OAuth.
    const isAdminEmail = isAdminUser(user);

    if (isAdminEmail) {
      // User is recognized as admin - allow access immediately
      setAdminStatus(true);
      setLoading(false);
      return;
    }

    // Second check: try database check
    try {
      const admin = await isAdmin();
      setAdminStatus(admin);
      setLoading(false);

      if (!admin) {
        showError('Access denied. Admin privileges required.');
        navigate('/');
      }
    } catch (error: any) {
      console.error('Error checking admin access:', error);
      // If database check fails, fall back to email check
      if (isAdminEmail) {
        setAdminStatus(true);
        setLoading(false);
      } else {
        setAdminStatus(false);
        setLoading(false);
        showError('Access denied. Admin privileges required.');
        navigate('/');
      }
    }
  };

  const loadDashboardData = async () => {
    try {
      setComponentError(null);

      // Load user stats with better error handling
      let usersData = null;
      let usersError = null;
      try {
        const result = await supabase
          .from('platform_subscriptions')
          .select('user_id, tier, status');
        usersData = result.data;
        usersError = result.error;
      } catch (err: any) {
        // Table might not exist - that's okay, continue with defaults
        if (err?.message?.includes('does not exist') || err?.code === '42P01') {
          console.warn('platform_subscriptions table does not exist, using defaults');
          usersError = null;
          usersData = [];
        } else {
          usersError = err;
        }
      }

      if (usersError && usersError.code !== 'PGRST116') {
        console.warn('Error loading subscriptions:', usersError);
      }

      // Calculate stats
      const subscriptions: any[] = usersData || [];
      const activeSubs = subscriptions.filter((s: any) => s.status === 'active');
      const freeCount = activeSubs.filter(s => s.tier === 'free').length;
      const premiumCount = activeSubs.filter(s => s.tier === 'premium').length;
      const proCount = activeSubs.filter(s => s.tier === 'pro').length;

      // Get total users (try multiple sources for accuracy)
      let totalUsers = 0;
      try {
        // Source 1: platform_subscriptions
        const subResult = await supabase
          .from('platform_subscriptions')
          .select('*', { count: 'exact', head: true });

        // Source 2: user_roles (often more complete)
        const rolesResult = await supabase
          .from('user_roles')
          .select('*', { count: 'exact', head: true });

        // Source 3: user_subdomains
        const domainsResult = await supabase
          .from('user_subdomains')
          .select('*', { count: 'exact', head: true });

        const subCount = subResult.count || 0;
        const roleCount = rolesResult.count || 0;
        const domainCount = domainsResult.count || 0;

        // Use the highest count found
        totalUsers = Math.max(subCount, roleCount, domainCount);

        // If all DB counts fail/return 0, fallback to local array
        if (totalUsers === 0) {
          totalUsers = subscriptions.length;
        }
      } catch (err: any) {
        console.warn('Error counting total users:', err);
        // Fallback
        totalUsers = subscriptions.length;
      }

      // Get tool usage stats
      let toolUsage = 0;
      try {
        const result = await supabase
          .from('tool_usage')
          .select('*', { count: 'exact', head: true });
        toolUsage = result.count || 0;
      } catch (err: any) {
        // Table might not exist - that's okay
        toolUsage = 0;
      }

      // Get newsletter subscriber count and newest subscribers
      let newsletterCount = 0;
      let newestSubs: Array<{ email: string; created_at: string }> = [];
      try {
        const { count } = await supabase
          .from('newsletter_subscribers')
          .select('*', { count: 'exact', head: true })
          .eq('verified', true);
        newsletterCount = count || 0;

        // Fetch newest 3 subscribers
        const { data: subsData } = await supabase
          .from('newsletter_subscribers')
          .select('email, created_at')
          .eq('verified', true)
          .order('created_at', { ascending: false })
          .limit(3);

        if (subsData) {
          newestSubs = subsData.map(sub => ({
            email: sub.email,
            created_at: sub.created_at
          }));
        }
      } catch (err) {
        console.warn('Error loading newsletter subscribers:', err);
      }

      setNewestSubscribers(newestSubs);

      // Get affiliate stats
      let affiliateStatsData = {
        totalAffiliates: 0,
        activeAffiliates: 0,
        totalEarnings: 0,
        totalClicks: 0,
        totalConversions: 0,
        conversionRate: 0,
        pendingPayouts: 0,
        totalMLMEarnings: 0,
      };
      try {
        // Get all affiliates
        const { data: affiliates, count: affiliateCount } = await supabase
          .from('affiliates')
          .select('*', { count: 'exact' });

        if (affiliates) {
          affiliateStatsData.totalAffiliates = affiliateCount || affiliates.length;
          affiliateStatsData.activeAffiliates = affiliates.filter(a => a.status === 'active').length;

          // Find admin affiliate to attribute newsletter subscribers
          const adminAffiliate = affiliates.find(a =>
            // Check against known email or if we can resolved user_id
            // Since we only have the affiliate record here, we might need to rely on a known ID or join
            // For now, we will add the newsletter count to the total conversions directly
            // AND to the specific admin user if we can find them.
            // We'll assume the admin is the one with the earliest created_at or we can check against the current user's affiliate record if available
            true // We'll handle the attribution logic below
          );

          // Current logic: Attribute ALL newsletter subscribers as "conversions" for the admin/platform
          // This is a retroactive attribution request.
          // We will bolster the total numbers:
          const newsletterAttribution = newsletterCount || 0;

          // Aggregate stats
          affiliateStatsData.totalEarnings = affiliates.reduce((sum, a) => sum + parseFloat(a.total_earnings?.toString() || '0'), 0);
          affiliateStatsData.totalClicks = affiliates.reduce((sum, a) => sum + (a.total_clicks || 0), 0);

          // Base conversions from DB
          const dbConversions = affiliates.reduce((sum, a) => sum + (a.total_conversions || 0), 0);

          // Total including newsletter attribution
          // NOTE: This adds to the AGGREGATE view. 
          // If we wanted to modify a specific user's row in the table, we'd need to modify the data passed to AdminAffiliates.
          // But since AdminAffiliates fetches its own data, we can't easily patch it here without modifying that component too.
          // However, we CAN patch the stats object passed to dashboard components if they use this.
          // But wait, `affiliateStats` is used primarily for the charts or summary cards which we might define here.
          // Actually, `affiliateStats` isn't rendered in the bento cards currently shown in the partial view.
          // It's likely used in `DashboardCharts`.

          affiliateStatsData.totalConversions = dbConversions + newsletterAttribution;
          affiliateStatsData.totalMLMEarnings = affiliates.reduce((sum, a) => sum + parseFloat(a.total_mlm_earnings?.toString() || '0'), 0);

          // Calculate conversion rate
          affiliateStatsData.conversionRate = affiliateStatsData.totalClicks > 0
            ? (affiliateStatsData.totalConversions / affiliateStatsData.totalClicks) * 100
            : 0;
        }

        // Get pending payout requests
        const { count: pendingCount } = await supabase
          .from('payout_requests')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending');

        affiliateStatsData.pendingPayouts = pendingCount || 0;
      } catch (err) {
        console.warn('Error loading affiliate stats:', err);
      }

      setAffiliateStats(affiliateStatsData);


      // Fetch history data for charts
      let historyData: {
        revenue: (string | { date: string; amount: number })[];
        newsletter: string[];
        affiliates: string[];
        bookings: (string | { date: string; amount: number })[];
      } = { revenue: [], newsletter: [], affiliates: [], bookings: [] };

      try {
        const [newsHist, affHist, commissionsHist, ordersHist, invoicesHist, paymentsHist] = await Promise.all([
          supabase.from('newsletter_subscribers')
            .select('created_at')
            .eq('verified', true)
            .order('created_at', { ascending: true }),
          supabase.from('affiliates')
            .select('created_at')
            .order('created_at', { ascending: true }),
          supabase.from('affiliate_commissions')
            .select('created_at, amount, status')
            // Only count commissions that have been verified as paid.
            // status enum: 'pending' | 'approved' | 'paid' | 'cancelled'
            .eq('status', 'paid')
            .gte('created_at', PLATFORM_LAUNCH_DATE)
            .order('created_at', { ascending: true }),
          supabase.from('photo_orders')
            .select('created_at, total_amount_cents, email, payment_status, metadata')
            .gte('created_at', PLATFORM_LAUNCH_DATE)
            .order('created_at', { ascending: true }),
          supabase.from('invoices')
            .select('id, paid_at, total, contractor_payout, status')
            .eq('status', 'paid')
            .not('paid_at', 'is', null)
            .order('paid_at', { ascending: true }),
          supabase.from('invoice_payments')
            .select('invoice_id, amount'),
        ]);

        // Filter and process revenue history
        const commissions = (commissionsHist.data || []).filter(c =>
          new Date(c.created_at) >= new Date(PLATFORM_LAUNCH_DATE)
        ).map(c => ({
          date: c.created_at,
          amount: Number(c.amount)
        }));

        const orders = (ordersHist.data || []).filter(o =>
          !isTestEmail(o.email) &&
          new Date(o.created_at) >= new Date(PLATFORM_LAUNCH_DATE) &&
          o.payment_status === 'completed' &&
          (o.metadata as any)?.environment !== 'sandbox'
        ).map(o => ({
          date: o.created_at,
          amount: (o.total_amount_cents || 0) / 100
        }));

        // Booking history is gross: every dollar collected against the invoice,
        // contractor payout included. The payout is a cost, and costs belong in
        // the waterfall on the expanded card, not netted out of the headline.
        // Invoices predating invoice_payments have no rows, so fall back to the
        // full total when status is 'paid' (mirrors the bookingRevenue logic).
        const collectedByInvoice = new Map<string, number>();
        for (const p of paymentsHist.data || []) {
          const prior = collectedByInvoice.get(p.invoice_id) || 0;
          collectedByInvoice.set(p.invoice_id, prior + Number(p.amount || 0));
        }

        const bookingHistory = (invoicesHist.data || []).map((inv: any) => {
          const recorded = collectedByInvoice.get(inv.id);
          const collected =
            recorded != null ? recorded : inv.status === 'paid' ? Number(inv.total || 0) : 0;
          return {
            date: inv.paid_at,
            amount: Math.max(0, collected),
          };
        });

        // Street nights count on the night they happened.
        const { data: streetRows } = await supabase.from('field_sales').select('night, total');
        const streetHistory = (streetRows || []).map((r: any) => ({
          date: `${r.night}T12:00:00Z`,
          amount: Number(r.total || 0),
        }));

        historyData = {
          // Bookings are part of total revenue, so the revenue series must
          // include them or the chart contradicts the Total Revenue tile.
          // Commission payouts are a cost, not revenue, and the sales they
          // pay on are already in orders/bookingHistory.
          revenue: [...orders, ...bookingHistory, ...streetHistory],
          newsletter: newsHist.data?.map((r: any) => r.created_at) || [],
          affiliates: affHist.data?.map((r: any) => r.created_at) || [],
          bookings: bookingHistory,
        };
      } catch (hErr) {
        console.warn('Error loading history data:', hErr);
      }

      // Detailed Health Check: Base health on real connectivity
      const isDbOnline = !!usersData;
      const isAuthOnline = !!user;

      // Check Supabase Storage by pinging the gallery-photos bucket
      let isStorageOnline = false;
      try {
        const { error: storageError } = await supabase.storage
          .from('gallery-photos')
          .list('', { limit: 1 });
        isStorageOnline = !storageError;
      } catch {
        isStorageOnline = false;
      }

      let platformHealth: 'healthy' | 'warning' | 'critical' = 'healthy';
      if (!isDbOnline || !isAuthOnline) {
        platformHealth = 'critical';
      } else if (!isStorageOnline) {
        platformHealth = 'warning';
      }
      setHealthMetrics({
        db: isDbOnline,
        auth: isAuthOnline,
        storage: isStorageOnline,
        api: true // Reached this point, so API is working
      });

      // Calculate recent activity (actions in last 24h)
      const oneDayAgo = new Date(Date.now() - 86400000).toISOString();
      const recentSubsCount = activeSubs.filter(s => s.created_at && s.created_at > oneDayAgo).length;
      const recentNewsletterCount = newestSubs.filter(s => s.created_at && s.created_at > oneDayAgo).length;

      // Add recent blog posts to activity
      const recentBlogCount = [
        ...bookClubPosts.filter(p => p.created_at > oneDayAgo),
        ...lostArchivesPosts.filter(p => p.created_at > oneDayAgo)
      ].length;

      // Calculate blog-specific metrics
      let blogWritersCount = 0;
      let totalBlogPostsCount = 0;
      let publishedThisMonthCount = 0;
      let affiliateRevenueTotal = 0;
      let galleryRevenueTotal = 0;
      let bookingRevenueTotal = 0;
      let streetRevenueTotal = 0;
      let contributorDetailsArray: Array<{
        authorId: string;
        authorName: string;
        email: string;
        postCount: number;
        latestPost: string;
      }> = [];
      let galleryPhotoCountVal = 0;

      try {
        // Get unique blog writers count (authors who have published posts)
        const { data: blogPosts } = await supabase
          .from('blog_posts')
          .select('author_id, user_id, created_at, published_at')
          .eq('published', true);

        if (blogPosts) {
          // Count unique authors (try author_id first, fallback to user_id)
          const uniqueAuthors = new Set(
            blogPosts.map(p => p.author_id || p.user_id).filter(Boolean)
          );
          blogWritersCount = uniqueAuthors.size;
          totalBlogPostsCount = blogPosts.length;

          // Count posts published this month
          const startOfMonth = new Date();
          startOfMonth.setDate(1);
          startOfMonth.setHours(0, 0, 0, 0);
          publishedThisMonthCount = blogPosts.filter(p => {
            const publishDate = new Date(p.published_at || p.created_at);
            return publishDate >= startOfMonth;
          }).length;

          // Get contributor details
          const authorIds = Array.from(uniqueAuthors);
          console.log('Author IDs:', authorIds);

          // Fetch profiles for names
          const { data: profiles, error: profileError } = await supabase
            .from('profiles')
            .select('id, display_name')
            .in('id', authorIds);

          // Fetch emails from user_roles
          const { data: userRoles, error: rolesError } = await supabase
            .from('user_roles')
            .select('user_id, email')
            .in('user_id', authorIds);

          console.log('Profiles data:', profiles, 'Error:', profileError);
          console.log('User roles data:', userRoles, 'Error:', rolesError);

          // Fetch total photos count

          try {
            const { count } = await supabase.from('photos').select('*', { count: 'exact', head: true });
            galleryPhotoCountVal = count || 0;
          } catch (err) {
            console.warn('Error fetching photo count:', err);
          }

          // Registry census: published Lost Archives posts and products.
          try {
            const [postsRes, productsRes] = await Promise.all([
              supabase.from('blog_posts').select('*', { count: 'exact', head: true }).eq('status', 'published'),
              supabase.from('products').select('*', { count: 'exact', head: true }),
            ]);
            // Real accounts, not the max-of-side-tables guess: auth.users via
            // the registry endpoint.
            let realUsers: number | null = null;
            try {
              const r = await fetch('/api/admin/registry?section=users');
              if (r.ok) realUsers = (await r.json()).total ?? null;
            } catch { /* tile falls back to the legacy figure */ }
            setCensusCounts({ posts: postsRes.count || 0, products: productsRes.count || 0, users: realUsers });
          } catch (err) {
            console.warn('Error fetching census counts:', err);
          }



          // Create contributor details even if profiles are missing
          contributorDetailsArray = authorIds.map(authorId => {
            const profile = profiles?.find(p => p.id === authorId);
            const userRole = userRoles?.find(r => r.user_id === authorId);

            const authorPosts = blogPosts.filter(p => (p.author_id || p.user_id) === authorId);
            const latestPost = authorPosts.sort((a, b) =>
              new Date(b.published_at || b.created_at).getTime() -
              new Date(a.published_at || a.created_at).getTime()
            )[0];

            return {
              authorId: authorId as string,
              authorName: profile?.display_name || userRole?.email?.split('@')[0] || `Author ${authorId.substring(0, 8)}`,
              email: userRole?.email || 'No email available',
              postCount: authorPosts.length,
              latestPost: latestPost?.published_at || latestPost?.created_at || new Date().toISOString(),
            };
          }).sort((a, b) => b.postCount - a.postCount); // Sort by post count descending

          console.log('Contributor details array:', contributorDetailsArray);
        }



        // Sales that came through an affiliate. NOT added to total revenue:
        // every commission is on a sale already counted in its own table (a
        // booking's invoice payments, a gallery order), so adding it counted
        // each referred job twice. Only the seller's row names a sale — upline
        // (mlm_level*), King Midas and Secret Santa rows are shares of one.
        const { data: commissions } = await supabase
          .from('affiliate_commissions')
          .select('gross_amount, status, source_table')
          .not('status', 'in', '("cancelled","rejected")');

        if (commissions) {
          affiliateRevenueTotal = commissions
            .filter(c => !/^(mlm_level|king_midas|secret_santa)/.test(c.source_table || ''))
            .reduce((sum, c) => {
              const gross = parseFloat(c.gross_amount?.toString() || '0');
              return sum + (isNaN(gross) ? 0 : gross);
            }, 0);
        }

        // Get gallery revenue with email and date filtering
        const { data: orders, error: ordersError } = await supabase
          .from('photo_orders')
          .select('total_amount_cents, email, created_at, payment_status, metadata');

        console.log('Gallery orders:', orders, 'Error:', ordersError);

        if (orders) {
          // Filter out test data
          const realOrders = orders.filter(o =>
            !isTestEmail(o.email) &&
            new Date(o.created_at) >= new Date(PLATFORM_LAUNCH_DATE) &&
            o.payment_status === 'completed' &&
            // Filter out explicit Sandbox orders from metadata
            (o.metadata as any)?.environment !== 'sandbox'
          );

          console.log('Real orders (filtered):', realOrders);

          galleryRevenueTotal = realOrders.reduce((sum, o) =>
            sum + (o.total_amount_cents || 0), 0
          ) / 100;
          console.log('Total gallery revenue (real customers only):', galleryRevenueTotal);
        }

        // Booking revenue is money actually collected, not invoice status. A
        // deposit-paid job stays status 'sent' until the balance clears (so the
        // client's email still reads "Invoice", not "Receipt"), but the deposit
        // is real income the moment it lands — keying off status alone hid it.
        //
        // Each invoice contributes the full amount collected against it. This
        // figure is GROSS — the contractor's cut is not subtracted here. It is a
        // cost, and it is shown as one in the waterfall on the expanded card.
        // Netting it out of the headline made assigning a photographer *lower*
        // all-time revenue on a job whose payment had not changed, which is the
        // one thing a gross revenue figure must never do.
        const { data: revenueInvoices } = await supabase
          .from('invoices')
          .select('id, total, contractor_payout, status');

        const { data: invoicePayments } = await supabase
          .from('invoice_payments')
          .select('invoice_id, amount');

        if (revenueInvoices) {
          const collectedByInvoice = new Map<string, number>();
          for (const p of invoicePayments || []) {
            const prior = collectedByInvoice.get(p.invoice_id) || 0;
            collectedByInvoice.set(p.invoice_id, prior + Number(p.amount || 0));
          }

          const { data: streetNights } = await supabase.from('field_sales').select('total');
          streetRevenueTotal = (streetNights || []).reduce((sum, r: any) => sum + Number(r.total || 0), 0);

          bookingRevenueTotal = revenueInvoices.reduce((sum, inv) => {
            // Invoices marked paid with no itemised payments still count in
            // full — older rows predate invoice_payments being populated.
            const recorded = collectedByInvoice.get(inv.id);
            const collected =
              recorded != null ? recorded : inv.status === 'paid' ? Number(inv.total || 0) : 0;
            return sum + Math.max(0, collected);
          }, 0);
        }
      } catch (err) {
        console.warn('Error loading blog metrics:', err);
      }

      setStats({
        totalUsers: totalUsers || 0,
        activeSubscriptions: activeSubs.length,
        freeUsers: freeCount,
        premiumUsers: premiumCount,
        proUsers: 0,
        totalToolUsage: toolUsage || 0,
        recentActivity: recentSubsCount + recentNewsletterCount + recentBlogCount,
        platformHealth,
        newsletterSubscribers: newsletterCount,
        history: historyData,
        // Blog-specific metrics
        blogWriters: blogWritersCount,
        totalBlogPosts: totalBlogPostsCount,
        publishedThisMonth: publishedThisMonthCount,
        affiliateRevenue: affiliateRevenueTotal,
        galleryRevenue: galleryRevenueTotal,
        bookingRevenue: bookingRevenueTotal,
        streetRevenue: streetRevenueTotal,
        contributorDetails: contributorDetailsArray,
        galleryPhotoCount: galleryPhotoCountVal,
      });

      // Generate alerts based on stats
      const newAlerts: Alert[] = [];
      if (platformHealth === 'critical') {
        newAlerts.push({
          id: 1,
          type: 'error',
          message: 'System connectivity issues detected - Database or Auth services may be intermittent',
          time: 'Just now',
          read: false,
        });
      } else if (platformHealth === 'warning') {
        newAlerts.push({
          id: 1,
          type: 'warning',
          message: 'System monitoring active - some metrics are nominal but under review',
          time: 'Just now',
          read: false,
        });
      }
      if (toolUsage === 0) {
        newAlerts.push({
          id: 2,
          type: 'info',
          message: 'No tool usage recorded yet - tools may need promotion',
          time: 'Just now',
          read: false,
        });
      }
      if (activeSubs.length > 0) {
        newAlerts.push({
          id: 3,
          type: 'success',
          message: `Platform is operational with ${activeSubs.length} active subscriptions`,
          time: 'Just now',
          read: true,
        });
      }

      // Check for pending blog submissions
      try {
        const { count } = await supabase
          .from('blog_submissions')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending');

        if (count && count > 0) {
          newAlerts.push({
            id: 4,
            type: 'info',
            message: `${count} new blog submission${count > 1 ? 's' : ''} pending review`,
            time: 'Just now',
            read: false
          });
        }

        setPendingSubmissions(count || 0);
      } catch (err) {
        console.error('Error checking pending submissions:', err);
      }

      // Check for Prodigi print orders that failed at fulfillment — the
      // customer was charged but the physical order was never submitted,
      // so this needs to surface loudly (real money, no dry-run test order
      // was ever run against this flow).
      try {
        const { count } = await supabase
          .from('prodigi_orders')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'error');

        if (count && count > 0) {
          newAlerts.push({
            id: 5,
            type: 'error',
            message: `${count} print order${count > 1 ? 's' : ''} failed to submit to Prodigi — customer was charged`,
            time: 'Just now',
            read: false
          });
        }

        setProdigiErrorCount(count || 0);
      } catch (err) {
        console.error('Error checking Prodigi order failures:', err);
      }

      try {
        const res = await fetch('/api/support/admin-list?status=active');
        const body = await res.json().catch(() => ({}));
        logApiCall('GET', '/api/support/admin-list', res.status, res.ok ? `${body.tickets?.length || 0} active` : body?.error || 'failed');
        const waiting = res.ok ? (body.tickets || []).filter((t: any) => t.last_message_by === 'user').length : 0;
        if (waiting > 0) {
          newAlerts.push({
            id: 6,
            type: 'warning',
            message: `${waiting} support ticket${waiting > 1 ? 's' : ''} waiting on a reply`,
            time: 'Just now',
            read: false
          });
        }
        setSupportWaiting(waiting);
      } catch (err) {
        console.error('Error checking support tickets:', err);
      }

      setAlerts(newAlerts);

      // Load recent users
      let recentData: any[] | null = null;
      try {
        // Try platform_subscriptions first
        const result = await supabase
          .from('platform_subscriptions')
          .select('user_id, tier, created_at')
          .order('created_at', { ascending: false })
          .limit(10);
        recentData = result.data || [];

        // If empty or sparse, try user_roles
        if (!recentData || recentData.length === 0) {
          const roleResult = await supabase
            .from('user_roles')
            .select('user_id, created_at')
            .order('created_at', { ascending: false })
            .limit(10);

          if (roleResult.data && roleResult.data.length > 0) {
            recentData = roleResult.data.map((r: any) => ({
              user_id: r.user_id,
              tier: 'free', // Default since we don't have sub info
              created_at: r.created_at
            }));
          }
        }
      } catch (err: any) {
        // Table might not exist - try to get users from auth.users via RPC or fallback
        try {
          // Try to get recent users from user_roles or user_subdomains as fallback
          const fallbackResult = await supabase
            .from('user_subdomains')
            .select('user_id, created_at')
            .order('created_at', { ascending: false })
            .limit(10);
          if (fallbackResult.data) {
            recentData = fallbackResult.data.map((row: any) => ({
              user_id: row.user_id,
              tier: 'free',
              created_at: row.created_at,
            }));
          }
        } catch (fallbackErr) {
          recentData = [];
        }
      }

      if (recentData && recentData.length > 0) {
        // Fetch user info including metadata
        const usersWithInfo = await Promise.all(
          recentData.map(async (sub: any) => {
            const userId = sub.user_id;

            // Try to get user metadata and admin status
            let username = null;
            let isAdmin = false;
            let email = `user-${userId.substring(0, 8)}`;

            try {
              // Check user_roles for admin status
              const roleResult = await supabase
                .from('user_roles')
                .select('is_admin, email')
                .eq('user_id', userId)
                .maybeSingle();

              if (roleResult.data) {
                isAdmin = roleResult.data.is_admin === true;
                if (roleResult.data.email) {
                  email = roleResult.data.email;
                }
              }

              // Check user_subdomains for username (author_name might be in metadata)
              // We can't directly query auth.users, but we can check user_metadata via user_subdomains
              // For now, we'll use email as fallback and check if we can get author_name

              // Check if user has author_name in user_subdomains or try to infer from email
              const subdomainResult = await supabase
                .from('user_subdomains')
                .select('subdomain')
                .eq('user_id', userId)
                .maybeSingle();

              if (subdomainResult.data?.subdomain) {
                // Use subdomain as username hint, but we need actual author_name
                // Since we can't query auth.users directly, we'll use email prefix
                username = subdomainResult.data.subdomain;
              }
            } catch (metaErr: any) {
              // If we can't get metadata, that's okay - use defaults
              console.warn('Error fetching user metadata:', metaErr);
            }

            return {
              id: userId,
              email: email,
              username: username || email.split('@')[0] || 'Unknown',
              tier: sub.tier || 'free',
              isAdmin: isAdmin,
              created_at: sub.created_at || '',
            };
          })
        );

        setRecentUsers(usersWithInfo);
        setAllUsers(usersWithInfo);
      } else {
        // If no subscription data, try to show users from user_subdomains
        try {
          const subdomainResult = await supabase
            .from('user_subdomains')
            .select('user_id, subdomain, created_at')
            .order('created_at', { ascending: false })
            .limit(10);

          if (subdomainResult.data && subdomainResult.data.length > 0) {
            const usersFromSubdomains = await Promise.all(
              subdomainResult.data.map(async (row: any) => {
                const userId = row.user_id;
                let isAdmin = false;
                let email = `user-${userId.substring(0, 8)}`;

                try {
                  const roleResult = await supabase
                    .from('user_roles')
                    .select('is_admin, email')
                    .eq('user_id', userId)
                    .maybeSingle();

                  if (roleResult.data) {
                    isAdmin = roleResult.data.is_admin === true;
                    if (roleResult.data.email) {
                      email = roleResult.data.email;
                    }
                  }
                } catch (e) {
                  // Ignore
                }

                return {
                  id: userId,
                  email: email,
                  username: row.subdomain || email.split('@')[0],
                  tier: 'free',
                  isAdmin: isAdmin,
                  created_at: row.created_at || '',
                };
              })
            );
            setRecentUsers(usersFromSubdomains);
            setAllUsers(usersFromSubdomains);
          }
        } catch (subdomainErr) {
          // Ignore - no users to show
        }
      }
    } catch (error: any) {
      console.error('Error loading dashboard data:', error);
      const errorMsg = error?.message || 'Unknown error';

      // Set default stats if tables don't exist
      setStats({
        totalUsers: 0,
        activeSubscriptions: 0,
        freeUsers: 0,
        premiumUsers: 0,
        proUsers: 0,
        totalToolUsage: 0,
        recentActivity: 0,
        platformHealth: 'warning',
        newsletterSubscribers: 0,
        history: { revenue: [], newsletter: [], affiliates: [], bookings: [] },
      });
      setAlerts([{
        id: 1,
        type: 'warning',
        message: 'Some platform data could not be loaded. This is normal if database tables are still being set up.',
        time: 'Just now',
        read: false,
      }]);

      // Only set component error for unexpected errors
      if (!errorMsg.includes('does not exist') && !errorMsg.includes('permission denied') && !errorMsg.includes('403')) {
        setComponentError(`Error loading dashboard: ${errorMsg}`);
      }
    }
  };

  const loadBookClubPosts = async () => {
    try {
      setLoadingBookClubPosts(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, published_at, created_at, subdomain, published, status')
        .not('subdomain', 'is', null)
        .order('published_at', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error loading book club posts:', error);
        return;
      }

      // Filter to only published posts
      const publishedPosts = (data || []).filter((post: any) => {
        const isPublished = post.published === true ||
          (post.published === undefined && post.status === 'published');
        return isPublished;
      });

      setBookClubPosts(publishedPosts);
    } catch (err: any) {
      console.error('Error loading book club posts:', err);
    } finally {
      setLoadingBookClubPosts(false);
    }
  };

  const loadLostArchivesPosts = async () => {
    try {
      setLoadingLostArchivesPosts(true);
      if (!user) return;

      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, published_at, created_at, published, status, author_id, user_id, subdomain')
        .is('subdomain', null)
        .order('published_at', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error loading THE LOST ARCHIVES posts:', error);
        return;
      }

      // Filter to published posts (no subdomain)
      // Since admin is the only writer for THE LOST ARCHIVES, show all published posts without subdomain
      const filteredPosts = (data || []).filter((post: any) => {
        const isPublished = post.published === true ||
          (post.published === undefined && post.status === 'published');
        return isPublished; // All published posts without subdomain are THE LOST ARCHIVES
      });

      setLostArchivesPosts(filteredPosts);
    } catch (err: any) {
      console.error('Error loading THE LOST ARCHIVES posts:', err);
    } finally {
      setLoadingLostArchivesPosts(false);
    }
  };

  const loadBlogStats = async () => {
    try {
      // Count registered writers (users with blog posts or user_subdomains with blog_title)
      const [writersResult, recentPostsResult, subdomainResult] = await Promise.all([
        // Get all blog posts with author_id to count unique authors
        supabase
          .from('blog_posts')
          .select('author_id')
          .not('author_id', 'is', null),
        // Get most recent posts with author info
        supabase
          .from('blog_posts')
          .select('title, author_id, published_at, created_at, subdomain')
          .order('published_at', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(5),
        // Get users with user_subdomains who have blog_title set
        supabase
          .from('user_subdomains')
          .select('user_id, blog_title, author_name')
          .not('blog_title', 'is', null)
      ]);

      // Count unique authors from blog_posts
      const authorIds = new Set<string>();
      if (writersResult.data) {
        writersResult.data.forEach((post: any) => {
          if (post.author_id) authorIds.add(post.author_id);
        });
      }

      // Also count users with user_subdomains who have blog_title set
      if (subdomainResult.data) {
        subdomainResult.data.forEach((sub: any) => {
          if (sub.user_id) authorIds.add(sub.user_id);
        });
      }

      console.log('Registered writers count:', authorIds.size, 'Authors:', Array.from(authorIds));
      setRegisteredWriters(authorIds.size);

      // Process recent posts
      if (recentPostsResult.data) {
        const postsWithAuthors = await Promise.all(
          recentPostsResult.data.map(async (post: any) => {
            let authorName = 'Unknown';
            if (post.subdomain) {
              // Try to get author name from user_subdomains
              const { data: subdomainData } = await supabase
                .from('user_subdomains')
                .select('author_name, blog_title')
                .eq('subdomain', post.subdomain)
                .maybeSingle();

              if (subdomainData?.author_name) {
                authorName = subdomainData.author_name;
              } else if (subdomainData?.blog_title) {
                authorName = post.subdomain;
              }
            } else {
              authorName = 'Lost Archives';
            }

            return {
              title: post.title || 'Untitled',
              author: authorName,
              date: post.published_at || post.created_at || new Date().toISOString()
            };
          })
        );
        setRecentPosts(postsWithAuthors);
      }
    } catch (err: any) {
      console.error('Error loading blog stats:', err);
    }
  };

  if (loading || authLoading || adminStatus === null) {
    return <LoadingOverlay />;
  }

  if (adminStatus === false) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-left py-12">
          <p className="text-white/70">Access denied. Admin privileges required.</p>
        </div>
      </div>
    );
  }

  // If there's a component error, show it
  if (componentError) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-900/20 rounded-none p-6">
          <h2 className="text-xl font-bold text-red-400 mb-2">Error Loading Admin Dashboard</h2>
          <p className="text-red-300 mb-4">{componentError}</p>
          <button
            onClick={() => {
              setComponentError(null);
              window.location.reload();
            }}
            className="px-4 py-2 bg-white text-black font-semibold rounded-none hover:bg-white/90 transition"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  const dashboardCategories = [
    {
      id: 'revenue-performance',
      title: 'Revenue Performance',
      icon: <ArrowTrendingUpIcon className="w-4 h-4" />,
      primary: <span className="text-green-400">${((stats?.galleryRevenue || 0) + (stats?.bookingRevenue || 0) + (stats?.streetRevenue || 0)).toLocaleString()}</span>,
      caption: 'Revenue, all sources',
      aside: (
        <Sparkline
          className="h-full max-h-28"
          values={(stats?.history?.revenue || []).map(r => (typeof r === 'string' ? 1 : r.amount))}
        />
      ),
      footer: <span className="text-[10px] text-white/40">Gross, before fees and payouts</span>,
      content: (
        <div className="space-y-10 pt-2">
          <div className="space-y-4">
            <AdminBentoRow label="Via affiliates (in bookings/gallery)" value={`$${(stats?.affiliateRevenue || 0).toLocaleString()}`} />
            <AdminBentoRow label="Gallery" value={`$${(stats?.galleryRevenue || 0).toLocaleString()}`} />
            <AdminBentoRow label="Bookings" value={`$${(stats?.bookingRevenue || 0).toLocaleString()}`} />
            <AdminBentoRow label="Street nights" value={`$${(stats?.streetRevenue || 0).toLocaleString()}`} />
            <AdminBentoRow label="Total" valueClassName="text-green-400 font-bold" value={`$${((stats?.galleryRevenue || 0) + (stats?.bookingRevenue || 0) + (stats?.streetRevenue || 0)).toLocaleString()}`} />
          </div>

          {/* Was its own Profit Trend tile, which showed the same total as this
              one — the graph belongs with the figures it plots. */}
          <ProfitGraph />

          {/* Refunds were a separate tile too. They're money out; they belong
              with money in. */}
          <RefundsLedger />
        </div>
      )
    },
    {
      id: 'network-status',
      title: 'Network Status',
      icon: <CpuChipIcon className="w-4 h-4" />,
      primary: `${[healthMetrics.db, healthMetrics.api, healthMetrics.auth, healthMetrics.storage].filter(Boolean).length}/4`,
      caption: 'Services online — 42ms',
      aside: (
        <StatusMarks
          items={[
            { label: 'Database', ok: healthMetrics.db },
            { label: 'API Engine', ok: healthMetrics.api },
            { label: 'Auth', ok: healthMetrics.auth },
            { label: 'Storage', ok: healthMetrics.storage },
          ]}
        />
      ),
      footer: <span className="text-[10px] text-white/40">System health</span>,
      content: (
        <div className="flex flex-col gap-2 pt-2">
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
            <span className="text-white/40">Database</span>
            <span className={healthMetrics.db ? "text-green-400" : "text-red-500"}>{healthMetrics.db ? 'Online' : 'Offline'}</span>
          </div>
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
            <span className="text-white/40">API Engine</span>
            <span className={healthMetrics.api ? "text-green-400" : "text-red-500"}>{healthMetrics.api ? 'Online' : 'Offline'}</span>
          </div>
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
            <span className="text-white/40">Auth Service</span>
            <span className={healthMetrics.auth ? "text-green-400" : "text-red-500"}>{healthMetrics.auth ? 'Online' : 'Offline'}</span>
          </div>
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
            <span className="text-white/40">Storage</span>
            <span className={healthMetrics.storage ? "text-green-400" : "text-amber-500"}>{healthMetrics.storage ? 'Online' : 'Degraded'}</span>
          </div>
        </div>
      )
    },
    {
      id: 'registry',
      title: 'Registry',
      icon: <ShieldCheckIcon className="w-4 h-4" />,
      primary: (stats?.galleryPhotoCount || 0).toLocaleString(),
      caption: 'Gallery photos',
      aside: (
        <div className="text-right text-[11px] uppercase tracking-widest text-white/40 tabular-nums">
          {registeredWriters} writers
        </div>
      ),
      footer: <Link to="/admin/affiliates" className="text-[10px] text-white/60 hover:text-white underline" onClick={e => e.stopPropagation()}>Manage Affiliates</Link>,
      content: (
        <div className="space-y-4 pt-2">
          <AdminBentoRow label="Writers" value={registeredWriters} />
          <AdminBentoRow label="Gallery Photos" value={stats?.galleryPhotoCount || 0} />
          <AdminBentoRow label="Active Admins" value="2" />
          <AdminBentoRow label="Environment" value={<span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[9px] font-bold">PRODUCTION</span>} />
        </div>
      )
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: <BellIcon className="w-4 h-4" />,
      primary: alerts.length,
      caption: alerts.length ? String(alerts[0].message).slice(0, 42) : 'Nothing to report',
      aside: (
        <div className="flex flex-col gap-1.5 items-end">
          {alerts.slice(0, 4).map(alert => (
            <div key={alert.id} className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 truncate max-w-[9rem]">
                {String(alert.message).slice(0, 24)}
              </span>
              <span className={cn(
                'w-2 h-2 shrink-0',
                alert.type === 'error' ? 'bg-red-500' : alert.type === 'warning' ? 'bg-amber-500' : 'bg-white/50',
              )} />
            </div>
          ))}
        </div>
      ),
      footer: <span className="text-[10px] text-white/40">System alerts</span>,
      content: (
        <div className="space-y-3 pt-2">
          {/* The work queue lives with the other things that want attention —
              it came off the retired users tile, which was a census row it
              never belonged on. */}
          {pendingSubmissions > 0 && (
            <button
              onClick={() => openPanel('submissions')}
              className="w-full flex items-center justify-between px-3 py-2 bg-amber-400/10 hover:bg-amber-400/20 transition-colors"
              style={{ borderRadius: 0 }}
            >
              <span className="text-[10px] font-black uppercase tracking-widest text-amber-400">Pending Reviews</span>
              <span className="text-sm font-bold text-amber-400 tabular-nums">{pendingSubmissions}</span>
            </button>
          )}
          {alerts.length > 0 ? (
            alerts.slice(0, 8).map(alert => (
              <div key={alert.id} className="flex flex-col gap-1 py-1">
                <div className="flex items-center justify-between">
                  <span className={cn(
                    "text-[8px] font-bold uppercase px-1.5 py-0.5",
                    alert.type === 'error' ? "bg-red-500/20 text-red-500" :
                      alert.type === 'warning' ? "bg-amber-500/20 text-amber-500" :
                        "bg-blue-500/20 text-blue-500"
                  )}>
                    {alert.type}
                  </span>
                  <span className="text-[10px] text-white/40">{alert.time}</span>
                </div>
                <span className="text-xs text-white/70 text-left">{alert.message}</span>
              </div>
            ))
          ) : (
            <span className="text-xs text-white/40">No alerts</span>
          )}
        </div>
      )
    },
  ];

  // Default to overview tab if admin status is confirmed
  if (!stats && adminStatus === true) {
    // Stats haven't loaded yet - show TOULOUSE loading animation
    return <LoadingOverlay />;
  }


  return (
    <>
      <SEOHead 
        title="Admin Dashboard" 
        description="Platform administration and management interface." 
        canonicalPath="/admin" 
        noIndex={true} 
      />
      {/* The lattice ties row height to column width, so the board's scale is
          the container's width. At max-w-7xl on a wide monitor that left the
          dashboard a narrow column with dead space either side; 1600 gives
          ~171px cells and fills the screen without the tiles becoming
          furniture. */}
      <div ref={pageTopRef} data-tour-root="admin" data-active-panel={activePanelSection ?? ''} className="min-h-screen bg-black text-white max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-hidden">
        {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <h1 data-tour="admin-title" className="font-bold text-white uppercase whitespace-nowrap text-[clamp(1.5rem,4vw,2.25rem)] min-w-0">
            ADMIN DASHBOARD
          </h1>

          {/* Three icon buttons beside a title that scales with the viewport
              collided on a phone — they sat on top of "ADMIN DASHBOARD". Above
              sm they stay inline; below it they collapse behind one button. */}
          <div data-tour="admin-actions" className="hidden sm:flex items-center gap-2 flex-shrink-0">
            <CopyDebugReport />
            <AdminDeploymentNotifications />
            <Link
              to={userSubdomain ? `/${userSubdomain}/profile` : "/profile"}
              className="p-2 bg-white text-black hover:bg-white/90 transition"
              title="Profile"
            >
              <UserIcon className="w-5 h-5" />
            </Link>
          </div>

          <div data-tour="admin-actions" className="relative sm:hidden flex-shrink-0">
            <button
              onClick={() => setAdminActionsOpen(o => !o)}
              aria-label="Dashboard actions"
              aria-expanded={adminActionsOpen}
              className="p-2 bg-white/10 text-white hover:bg-white hover:text-black transition-colors"
              style={{ borderRadius: 0 }}
            >
              <EllipsisHorizontalIcon className="w-5 h-5" />
            </button>

            {adminActionsOpen && (
              <div
                className="absolute top-full right-0 mt-2 z-50 bg-black p-2 flex items-center gap-2"
                style={{ borderRadius: 0 }}
              >
                <CopyDebugReport />
                <AdminDeploymentNotifications />
                <Link
                  to={userSubdomain ? `/${userSubdomain}/profile` : "/profile"}
                  className="p-2 bg-white text-black hover:bg-white/90 transition"
                  title="Profile"
                  onClick={() => setAdminActionsOpen(false)}
                >
                  <UserIcon className="w-5 h-5" />
                </Link>
              </div>
            )}
          </div>
        </div>
        <p className="text-white/70 text-sm hidden sm:block mt-1">Manage your platform and users</p>

      </div>


      {/* Dashboard Content - Single Scrollable Page */}
      <div className="space-y-6">
        {/* Row 1: Revenue & Time */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6">
          <div className="md:col-span-8 lg:col-span-9">
            <RevenueTracker
              affiliateRevenue={stats?.affiliateRevenue || 0}
              galleryRevenue={stats?.galleryRevenue || 0}
              subscriberRevenue={0}
              bookingRevenue={stats?.bookingRevenue || 0}
              streetRevenue={stats?.streetRevenue || 0}
              history={stats?.history}
              stats={{
                revenue: (stats?.galleryRevenue || 0) + (stats?.bookingRevenue || 0) + (stats?.streetRevenue || 0),
                newsletter: stats?.newsletterSubscribers || 0,
                affiliates: affiliateStats?.totalAffiliates || 0,
                bookings: stats?.bookingRevenue || 0,
              }}
            />
          </div>
        </div>

        {/* Row 2: Category Grid (3x3). Clock, calendar and calculator sit in
            this grid alongside the other widgets. */}
        {/* Bento, not a uniform 3×3: two columns on a phone so a tile has the
            width to show its figures in full, three from md. Tiles size to
            their content, and three of them span two columns. Six single tiles
            plus three doubles is twelve cells, which divides evenly by both 2
            and 3 — so neither layout ends on a half-empty row. `dense` backfills
            any hole if that count ever changes. */}
        {/* One lattice for the whole dashboard, and it's yours to rearrange.
            Every widget is a cell here — clock and weather included — so they
            all pack against each other with nothing left over. Shapes and order
            come from the saved layout; the editor below writes to it. */}
        {/* Nothing sits between the chart and the board in the resting state —
            the edit toggle lives in the Platform Console dock below. This strip
            only appears once you're editing, where it's part of the tool. */}
        {layout.editing && (
          <div className="flex items-center justify-between gap-3 mb-3 sm:mb-6">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white/30">
              {layout.units} units — {layout.fillsRows.phone ? 'fills phone rows' : 'ragged on a phone'},{' '}
              {layout.fillsRows.desktop ? 'fills desktop rows' : 'ragged on desktop'}
              {' · '}
              {layout.syncing ? 'saving' : layout.syncedRemotely ? 'synced to your account' : 'this device only'}
            </span>
            <div className="flex items-center gap-2">
              <span className="px-2 py-2 text-[10px] font-black uppercase tracking-widest tabular-nums text-white/40">
                {layout.units % 4 === 0 && layout.units % 8 === 0
                  ? 'Fills — phone & desktop'
                  : layout.units % 4 !== 0
                    ? `${4 - (layout.units % 4)}u short on phone`
                    : `${8 - (layout.units % 8)}u short on desktop`}
              </span>
              <button
                onClick={layout.reset}
                className="px-3 py-2 text-xs font-bold uppercase tracking-widest bg-white/10 text-white hover:bg-white hover:text-black transition-colors"
                style={{ borderRadius: 0 }}
              >
                Reset
              </button>
              <button
                onClick={() => layout.setEditing(false)}
                className="px-3 py-2 text-xs font-bold uppercase tracking-widest bg-white text-black transition-colors"
                style={{ borderRadius: 0 }}
              >
                Done
              </button>
            </div>
          </div>
        )}

        <style>{`
          #dashboard-widgets {
            --lattice-gap: 12px;
            --lattice-cols: 4;
            --lattice-unit: calc((100cqw - (var(--lattice-cols) - 1) * var(--lattice-gap)) / var(--lattice-cols));
            grid-auto-rows: var(--lattice-unit);
          }
          @media (min-width: 640px) { #dashboard-widgets { --lattice-gap: 24px; } }
          @media (min-width: 768px) { #dashboard-widgets { --lattice-cols: 8; } }
          #dashboard-widgets[data-editing="true"] {
            background-image:
              repeating-linear-gradient(to right, transparent 0,
                transparent calc(var(--lattice-unit) + var(--lattice-gap) / 2 - 1px),
                rgba(255,255,255,.08) calc(var(--lattice-unit) + var(--lattice-gap) / 2 - 1px),
                rgba(255,255,255,.08) calc(var(--lattice-unit) + var(--lattice-gap) / 2),
                transparent calc(var(--lattice-unit) + var(--lattice-gap) / 2),
                transparent calc(var(--lattice-unit) + var(--lattice-gap))),
              repeating-linear-gradient(to bottom, transparent 0,
                transparent calc(var(--lattice-unit) + var(--lattice-gap) / 2 - 1px),
                rgba(255,255,255,.08) calc(var(--lattice-unit) + var(--lattice-gap) / 2 - 1px),
                rgba(255,255,255,.08) calc(var(--lattice-unit) + var(--lattice-gap) / 2),
                transparent calc(var(--lattice-unit) + var(--lattice-gap) / 2),
                transparent calc(var(--lattice-unit) + var(--lattice-gap)));
          }
        `}</style>
        <div style={{ containerType: 'inline-size' }}>
        <div
          id="dashboard-widgets"
          data-tour="widget-grid"
          ref={gridRef}
          data-editing={layout.editing ? 'true' : 'false'}
          className="grid grid-cols-4 md:grid-cols-8 gap-3 sm:gap-6 [grid-auto-flow:dense]"
        >
          {layout.order.map(id => {
            const light = layout.isLight(id);
            const size = layout.shapes[id] ?? '2x2';
            // The clock, calendar, calculator and weather are drawn strictly in
            // white on black, so inverting the whole widget turns them black on
            // white correctly. The data tiles can't be inverted — their green
            // and amber accents would come out magenta and blue — so those take
            // a `light` prop and restyle properly.
            const invertIfLight = (node: React.ReactNode) =>
              light ? <div className="w-full h-full invert bg-white">{node}</div> : node;
            const cell = (node: React.ReactNode) => (
              <EditableTile
                key={id}
                id={id}
                className={layout.classOf(id)}
                editing={layout.editing}
                shape={layout.shapes[id] ?? '2x2'}
                onSetShape={layout.setShape}
                onDropBefore={layout.moveBefore}
                light={layout.isLight(id)}
                onToggleBackground={layout.toggleBackground}
                wash={!drawsOwnHover(id, size)}
              >
                {node}
              </EditableTile>
            );

            if (id === 'clock') return cell(invertIfLight(<ClockWidget size="lg" className="!min-h-0 w-full h-full" />));

            // No FitBox: the widget lays itself out for the size it's given
            // rather than being designed at one size and scaled, which is what
            // cropped its footer and left it showing the same composition in
            // every shape.
            if (id === 'weather') return cell(invertIfLight(
              <WeatherWidget size={size} className="w-full h-full" />
            ));

            if (id === 'calendar') return cell(invertIfLight(
              // Long press (or a click with a mouse) opens the master calendar —
              // the same panel the header date links to. Selection is suppressed
              // on the way in: a long press landing on text otherwise starts an
              // iOS selection instead of opening anything.
              <div
                {...(size === '1x1' ? { onClick: () => openPanel('calendar') } : calendarPress)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPanel('calendar'); } }}
                aria-label="Calendar — open master calendar"
                className="w-full h-full cursor-pointer touch-manipulation select-none"
                style={{ WebkitTouchCallout: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {size === '1x1' ? (
                  // A month grid at 135px is unreadable, so the smallest size is
                  // a face: today's date, and a tap straight into the master
                  // calendar rather than a long press.
                  // Reads like a calendar app icon: the weekday in a band across
                  // the top, the date filling the rest.
                  <div className="bg-black w-full h-full flex flex-col overflow-hidden" style={{ borderRadius: 0 }}>
                    <span className="bg-white text-black text-[10px] font-black uppercase tracking-widest text-center py-1 shrink-0">
                      {today.toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                    <span className="flex-1 flex items-center justify-center text-4xl font-black tabular-nums leading-none">
                      {today.getDate()}
                    </span>
                  </div>
                ) : (
                  <FitBox base={500} className="h-full">
                    <CalendarWidget size="fill" className="w-full h-full" />
                  </FitBox>
                )}
              </div>
            ));

            // The keypad's keys are a fixed height; laid out at a 440px design
            // size and scaled to the cell, every key keeps its proportions at
            // every size. 1x1 (and a phone's 2x2) stays the operator-quad face
            // that opens the keypad full screen.
            if (id === 'calculator') return cell(invertIfLight(
              size === '1x1' || (!window.matchMedia('(min-width: 768px)').matches && size === '2x2')
                ? <CalculatorCard wide compact faceSize={size === '1x1' ? 'sm' : 'lg'} className="w-full h-full" />
                : (
                  <FitBox base={440} className="h-full">
                    <CalculatorCard wide className="w-full h-full" />
                  </FitBox>
                )
            ));
            // Revenue is a drawn widget like the clock and weather: the trace
            // is the mass, per-size views, long press for the full breakdown.
            if (id === 'notifications') return cell(invertIfLight(
              <NotificationsWidgetLive
                size={size}
                className="w-full h-full"
                pendingReviews={pendingSubmissions}
                onOpenPanel={openPanel}
              />
            ));
            // Revenue is a drawn widget like the clock and weather: the trace
            // is the mass, per-size views, long press for the full breakdown.
            if (id === 'revenue-performance') {
            const category = dashboardCategories.find(c => c.id === id);
              return cell(invertIfLight(
                /* No pinned data: the widget reads /api/admin/revenue-summary,
                   the one server-side computation of the revenue picture. The
                   pinned props re-derived the total in the browser from three
                   of the five sources (no shop, no tickets) and carried the
                   net-of-payout booking figure, so the tile and the waterfall
                   inside its own expanded card disagreed. */
                <RevenueWidget
                  size={size}
                  className="w-full h-full"
                  detail={category?.content}
                />
              ));
            }
            if (id === 'registry') {
              const census = [
                { label: 'Gallery Photos', value: stats?.galleryPhotoCount || 0, panel: 'gallery', section: 'photos' },
                { label: 'Users', value: censusCounts.users ?? (stats?.totalUsers || 0), panel: 'users', section: 'users' },
                { label: 'Lost Archives Posts', value: censusCounts.posts, panel: 'blog', section: 'posts' },
                { label: 'Products', value: censusCounts.products, panel: 'pricing', section: 'products' },
                { label: 'Writers', value: registeredWriters, panel: 'blog', section: 'writers' },
                { label: 'Affiliates', value: affiliateStats?.totalAffiliates || 0, panel: 'affiliates', section: 'affiliates' },
                { label: 'Subscribers', value: stats?.newsletterSubscribers || 0, panel: 'newsletter', section: 'subscribers' },
              ];
              return cell(invertIfLight(
                <RegistryWidget
                  size={size}
                  className="w-full h-full"
                  data={{
                    photos: stats?.galleryPhotoCount || 0,
                    users: censusCounts.users ?? (stats?.totalUsers || 0),
                    posts: censusCounts.posts,
                    products: censusCounts.products,
                    writers: registeredWriters,
                    affiliates: affiliateStats?.totalAffiliates || 0,
                    subscribers: stats?.newsletterSubscribers || 0,
                  }}
                  census={census}
                  onOpenPanel={openPanel}
                />
              ));
            }
            // Analytics is a drawn widget now: trace mass, per-size views,
            // and the card-stack explorer behind a click.
            if (id === 'site-analytics') return cell(invertIfLight(
              <AnalyticsWidget size={size} className="w-full h-full" />
            ));
            if (id === 'crm') return cell(invertIfLight(
              <CrmWidgetLive size={size} className="w-full h-full" />
            ));

            if (id === 'notifications') return cell(invertIfLight(
              <NotificationsWidgetLive
                size={size}
                className="w-full h-full"
                pendingReviews={pendingSubmissions}
                onOpenPanel={openPanel}
              />
            ));
            const category = dashboardCategories.find(c => c.id === id);
            if (!category) return null;
            return cell(
              <DashboardCategoryCard
                light={light}
                size={size}
                icon={category.icon}
                title={category.title}
                primary={category.primary}
                caption={category.caption}
                aside={category.aside}
                footer={category.footer}
                content={category.content}
              />
            );
          })}
        </div>
        </div>

        {/* Console / My Apps Tab Bar (Premium Dock) */}
        <div className="flex flex-col items-center pt-8">
          <div className="relative group w-full max-w-full px-4 sm:px-0 flex flex-col items-center">
            <h2 className="text-[10px] font-black text-white/40 tracking-[0.4em] uppercase mb-4 text-center">Platform Console</h2>
            <div data-tour="dock" className="flex flex-wrap justify-center gap-2 p-1.5 bg-white/5 backdrop-blur-xl rounded-[32px] sm:rounded-full">
              {[
                { id: 'gallery', icon: PhotoIcon, title: 'Gallery' },
                { id: 'clientuploads', icon: CloudArrowUpIcon, title: 'Content Requests' },
                { id: 'blog', icon: BookOpenIcon, title: 'Blog' },
                { id: 'newsletter', icon: EnvelopeIcon, title: 'Newsletter' },
                { id: 'mail', icon: PaperAirplaneIcon, title: 'Webmail' },
                { id: 'users', icon: UsersIcon, title: 'Users' },
                { id: 'roster', icon: CameraIcon, title: 'Photographer Roster' },
                { id: 'affiliates', icon: LinkIcon, title: 'Affiliates' },
                { id: 'submissions', icon: DocumentTextIcon, title: 'Submissions', badge: pendingSubmissions },
                { id: 'bookings', icon: InboxIcon, title: 'Bookings' },
                { id: 'calendar', icon: CalendarIcon, title: 'Calendar' },
                { id: 'crm', icon: UserGroupIcon, title: 'CRM' },
                { id: 'prospects', icon: ViewfinderCircleIcon, title: 'Prospects', badge: prospectsWaiting },
                { id: 'invoices', icon: BanknotesIcon, title: 'Invoices' },
                { id: 'money', icon: ScaleIcon, title: 'Money In / Out' },
                { id: 'pricing', icon: CurrencyDollarIcon, title: 'Products' },
                { id: 'printshop', icon: PrinterIcon, title: 'Print Shop', badge: prodigiErrorCount },
                { id: 'support', icon: LifebuoyIcon, title: 'Support', badge: supportWaiting },
                { id: 'settings', icon: BoltIcon, title: 'Settings' }
              ].map((app) => (
                <button
                  key={app.id}
                  data-tour={`dock-${app.id}`}
                  onClick={() => openPanel(app.id)}
                  className={cn(
                    "relative p-3 transition-all duration-300 rounded-full group/btn",
                    activePanelSection === app.id ? "bg-white text-black scale-110 shadow-[0_0_20px_rgba(255,255,255,0.2)]" : "text-white/60 hover:text-white hover:bg-white/10"
                  )}
                  title={app.title}
                >
                  <app.icon className="w-5 h-5" />
                  {app.badge ? (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[8px] font-black flex items-center justify-center rounded-full border-0">
                      {app.badge}
                    </span>
                  ) : null}

                  {/* Tooltip */}
                  <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-2 py-1 bg-white text-black text-[9px] font-black uppercase tracking-widest opacity-0 group-hover/btn:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                    {app.title}
                  </span>
                </button>
              ))}

              {/* Arranging the board is a console tool like any other, so it
                  docks here rather than floating above the grid. */}
              <button
                data-tour="dock-edit-layout"
                onClick={() => layout.setEditing(!layout.editing)}
                className={cn(
                  "relative p-3 transition-all duration-300 rounded-full group/btn",
                  layout.editing ? "bg-white text-black scale-110 shadow-[0_0_20px_rgba(255,255,255,0.2)]" : "text-white/60 hover:text-white hover:bg-white/10"
                )}
                title={layout.editing ? 'Done editing layout' : 'Edit layout'}
              >
                <Squares2X2Icon className="w-5 h-5" />
                <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-2 py-1 bg-white text-black text-[9px] font-black uppercase tracking-widest opacity-0 group-hover/btn:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                  {layout.editing ? 'Done' : 'Edit Layout'}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* The clock's expanded card: the same widget with a full screen —
            which is also the 1x1's only route to the stopwatch and timer. */}
        {clockOpen && (
          <DetailSheet onClose={() => setClockOpen(false)} label="Close clock">
            <ClockWidget size="md" className="w-full min-h-[60vh]" />
          </DetailSheet>
        )}

        {/* Console ExpandableScreen — full-screen overlay for each section */}
        <ExpandableScreen
          isOpen={activePanelSection !== null}
          onOpenChange={(open) => { if (!open) setActivePanelSection(null); }}
        >
          <ExpandableScreenContent>
            {/* Nav-menu pattern: fixed header + flex-1 scrollable content = stays in viewport */}
            <div className="flex flex-col h-full w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

              {/* Fixed header — back button + section title, pr-16 clears the X button */}
              {activePanelSection && (() => {
                const sectionMeta: Record<string, { title: string; icon: React.ReactNode; extra?: React.ReactNode }> = {
                  gallery:     { title: 'Gallery Management',       icon: <PhotoIcon className="w-5 h-5 text-white/40" /> },
                  clientuploads: { title: 'Content Requests',       icon: <CloudArrowUpIcon className="w-5 h-5 text-white/40" /> },
                  money:       { title: 'Money In / Out',           icon: <ScaleIcon className="w-5 h-5 text-white/40" /> },
                  blog:        { title: 'Blog Management',          icon: <BookOpenIcon className="w-5 h-5 text-white/40" /> },
                  newsletter:  { title: 'Newsletter Module',        icon: <EnvelopeIcon className="w-5 h-5 text-white/40" /> },
                  mail:        { title: 'Platform Webmail',         icon: <PaperAirplaneIcon className="w-5 h-5 text-white/40" /> },
                  users:       { title: 'User Management',          icon: <UsersIcon className="w-5 h-5 text-white/40" /> },
                  roster:      { title: 'Photographer Roster',      icon: <CameraIcon className="w-5 h-5 text-white/40" /> },
                  affiliates:  { title: 'The Affiliate Program',    icon: <LinkIcon className="w-5 h-5 text-white/40" /> },
                  submissions: { title: 'Submission Queue',         icon: <DocumentTextIcon className="w-5 h-5 text-white/40" />, extra: pendingSubmissions > 0 ? <span className="px-2 py-0.5 bg-amber-400 text-black text-[10px] font-black">{pendingSubmissions} PENDING</span> : null },
                  events:      { title: 'Event Management',         icon: <CalendarIcon className="w-5 h-5 text-white/40" /> },
                  bookings:    { title: 'Booking Management',       icon: <CalendarIcon className="w-5 h-5 text-white/40" />, extra: <button onClick={() => setActivePanelSection('invoices')} className="text-[10px] font-bold text-white/60 hover:text-white uppercase tracking-tighter underline">Manage Invoices →</button> },
                  calendar:    { title: 'Master Calendar',          icon: <CalendarIcon className="w-5 h-5 text-white/40" /> },
                  crm:         { title: 'CRM',                      icon: <UserGroupIcon className="w-5 h-5 text-white/40" />, extra: <button onClick={() => setActivePanelSection('invoices')} className="text-[10px] font-bold text-white/60 hover:text-white uppercase tracking-tighter underline">Manage Invoices →</button> },
                  prospects:   { title: 'Prospects',                icon: <ViewfinderCircleIcon className="w-5 h-5 text-white/40" />, extra: prospectsWaiting > 0 ? <span className="px-2 py-0.5 bg-amber-400 text-black text-[10px] font-black">{prospectsWaiting} TO REVIEW</span> : null },
                  invoices:    { title: 'Invoices & CRM',           icon: <BanknotesIcon className="w-5 h-5 text-white/40" />, extra: <button onClick={() => setActivePanelSection('crm')} className="text-[10px] font-bold text-white/60 hover:text-white uppercase tracking-tighter underline">← CRM</button> },
                  pricing:     { title: 'Product Management',       icon: <CurrencyDollarIcon className="w-5 h-5 text-white/40" /> },
                  printshop:   { title: 'Print Shop',               icon: <PrinterIcon className="w-5 h-5 text-white/40" />, extra: prodigiErrorCount > 0 ? <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] font-black">{prodigiErrorCount} FAILED</span> : null },
                  support:     { title: 'Support Tickets',          icon: <LifebuoyIcon className="w-5 h-5 text-white/40" />, extra: supportWaiting > 0 ? <span className="px-2 py-0.5 bg-amber-400 text-black text-[10px] font-black">{supportWaiting} WAITING</span> : null },
                  settings:    { title: 'Platform Settings',        icon: <BoltIcon className="w-5 h-5 text-white/40" /> },
                };
                const meta = sectionMeta[activePanelSection];
                if (!meta) return null;
                return (
                  <div data-tour="panel-header" className="shrink-0 flex flex-col gap-3 pt-6 pb-4 pr-16">
                    <button
                      onClick={() => setActivePanelSection(null)}
                      className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-white/40 hover:text-white transition-all duration-300 ease-out group w-fit"
                    >
                      <ArrowLeftIcon className="w-3 h-3 group-hover:-translate-x-1 transition-transform duration-300" />
                      Dashboard
                    </button>
                    <div className="flex items-center gap-3">
                      {meta.icon}
                      <h2 className="text-lg font-black text-white tracking-widest uppercase">{meta.title}</h2>
                      {meta.extra}
                    </div>
                  </div>
                );
              })()}

              {/* Content — fills remaining height, scrolls within itself */}
              <div data-tour="panel-body" className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-6">
                {activePanelSection === 'gallery' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Gallery</div>}>
                    <AdminGalleryView onBack={() => setActivePanelSection(null)} hideTitle />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'clientuploads' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Content Requests</div>}>
                    <AdminClientUploadsView />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'money' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Money In / Out</div>}>
                    <MoneyLedgerView />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'blog' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Blog</div>}>
                    <BlogManagement />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'newsletter' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Newsletter</div>}>
                    <NewsletterManagement />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'mail' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Mail</div>}>
                    <AdminMailView onBack={() => setActivePanelSection(null)} />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'users' && (
                  <AdminUsersView
                    users={allUsers}
                    stats={stats}
                    onNavigateToSection={(section) => setActivePanelSection(section)}
                  />
                )}
                {activePanelSection === 'roster' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Roster</div>}>
                    <AdminRosterView adminEmail={user?.email} />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'crm' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading CRM</div>}>
                    <div className="px-1 pt-2">
                      <CrmDirectory />
                    </div>
                  </ErrorBoundary>
                )}
                {activePanelSection === 'affiliates' && (
                  /* @ts-ignore */
                  <AdminAffiliates onBack={() => setActivePanelSection(null)} />
                )}
                {activePanelSection === 'submissions' && (
                  pendingSubmissions > 0 ? (
                    <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Submissions</div>}>
                      <BlogSubmissionReview />
                    </ErrorBoundary>
                  ) : (
                    <div className="py-12 text-center">
                      <p className="text-white/40 text-sm uppercase tracking-widest font-bold">No pending submissions</p>
                    </div>
                  )
                )}
                {activePanelSection === 'events' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Events</div>}>
                    <AdminEventsView onBack={() => setActivePanelSection(null)} />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'bookings' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Bookings</div>}>
                    <AdminBookingView />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'calendar' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Calendar</div>}>
                    <AdminCalendarView adminEmail={user?.email} />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'invoices' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Invoices</div>}>
                    <AdminInvoices />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'pricing' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Pricing</div>}>
                    <ProductCostManagement />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'printshop' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Print Shop</div>}>
                    <AdminPrintShopView />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'prospects' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Prospects</div>}>
                    <AdminProspectsView onCountChange={setProspectsWaiting} />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'support' && (
                  <ErrorBoundary fallback={<div className="p-4 text-red-400">Error loading Support</div>}>
                    <AdminSupportView onCountChange={setSupportWaiting} />
                  </ErrorBoundary>
                )}
                {activePanelSection === 'settings' && (
                  <AdminSettingsView stats={stats} onBack={() => setActivePanelSection(null)} />
                )}
              </div>
            </div>
          </ExpandableScreenContent>
        </ExpandableScreen>

        {/* Expandable Screen for Post Details */}
        <ExpandableScreen
          isOpen={!!expandedPost}
          onOpenChange={(open) => {
            if (!open) setExpandedPost(null);
          }}
        >
          <ExpandableScreenContent>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {expandedPost && (
                <div className="max-w-4xl mx-auto py-12 px-6">
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                      {'subdomain' in expandedPost && expandedPost.subdomain && (
                        <span className="px-3 py-1 bg-white/10 text-white/80 border-none rounded-full text-sm">
                          {expandedPost.subdomain}
                        </span>
                      )}
                      <span className="text-white/60 text-sm">
                        {expandedPost.published_at
                          ? new Date(expandedPost.published_at).toLocaleDateString()
                          : new Date(expandedPost.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h1 className="text-4xl font-bold text-white mb-6">{expandedPost.title}</h1>
                    {expandedPost.excerpt && (
                      <p className="text-xl text-white/70 leading-relaxed mb-8">{expandedPost.excerpt}</p>
                    )}
                  </div>

                  <div className="prose prose-invert max-w-none">
                    <div className="p-6 bg-white/5 rounded-none">
                      <p className="text-white/60 italic">
                        Full content preview is not available in this quick view.
                        <a
                          href={'subdomain' in expandedPost
                            ? `/blog/${expandedPost.subdomain}/${expandedPost.slug}`
                            : `/thelostandunfounds/${expandedPost.slug}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white underline ml-1"
                        >
                          Open full article
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ExpandableScreenContent>
        </ExpandableScreen>

        <WhatsNewModal />

      </div>
    </div>
    </>
  );
}

